import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import clsx from 'clsx';

import { joinIds } from '../internal/aria';
import styles from './Radio.module.css';

interface RadioGroupContextValue {
  name: string;
  value: string | undefined;
  onValueChange: ((value: string) => void) | undefined;
  disabled: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
  value: string;
}

/**
 * Native radios inside a `fieldset`: arrow-key roving between options, group
 * naming and forced-colours rendering are all the platform's job, and the
 * platform does them better than any re-implementation.
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, hint, value, className, ...rest },
  ref,
) {
  const group = useContext(RadioGroupContext);
  const generated = useId();
  const id = rest.id ?? `${generated}-radio`;
  const hintId = `${generated}-hint`;

  return (
    <div className={clsx(styles.root, className)}>
      <span className={styles.control}>
        <input
          {...rest}
          ref={ref}
          id={id}
          type="radio"
          value={value}
          name={rest.name ?? group?.name}
          checked={group === null ? rest.checked : group.value === value}
          disabled={rest.disabled ?? group?.disabled}
          className={styles.input}
          aria-describedby={joinIds(rest['aria-describedby'], hint !== undefined && hintId)}
          onChange={(event) => {
            rest.onChange?.(event);
            if (event.target.checked) group?.onValueChange?.(value);
          }}
        />
        <span className={styles.indicator} aria-hidden="true" />
      </span>
      <span className={styles.text}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {hint !== undefined && (
          <span className={styles.hint} id={hintId}>
            {hint}
          </span>
        )}
      </span>
    </div>
  );
});

export interface RadioGroupProps {
  /** The group question. Rendered as the fieldset legend. */
  label: ReactNode;
  /** Shared input name. Generated when omitted. */
  name?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  hint?: ReactNode;
  error?: ReactNode;
  orientation?: 'vertical' | 'horizontal';
  disabled?: boolean;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function RadioGroup({
  label,
  name,
  value,
  onValueChange,
  hint,
  error,
  orientation = 'vertical',
  disabled = false,
  required = false,
  children,
  className,
}: RadioGroupProps) {
  const generated = useId();
  const groupName = name ?? `${generated}-radiogroup`;
  const hintId = `${generated}-hint`;
  const errorId = `${generated}-error`;
  const invalid = error !== undefined && error !== null && error !== false;

  return (
    <fieldset
      className={clsx(styles.group, className)}
      aria-describedby={joinIds(hint !== undefined && hintId, invalid && errorId)}
      aria-invalid={invalid ? true : undefined}
      aria-required={required ? true : undefined}
      disabled={disabled}
    >
      <legend className={styles.legend}>{label}</legend>
      {hint !== undefined && (
        <p className={styles.groupHint} id={hintId}>
          {hint}
        </p>
      )}
      <div className={clsx(styles.options, orientation === 'horizontal' && styles.horizontal)}>
        <RadioGroupContext.Provider
          value={{ name: groupName, value, onValueChange, disabled }}
        >
          {children}
        </RadioGroupContext.Provider>
      </div>
      {invalid && (
        <p className={styles.groupError} id={errorId}>
          {error}
        </p>
      )}
    </fieldset>
  );
}
